import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Dialog,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  MyFormInput,
  MyFormSelect,
} from "@/components/zthd/MyFormField";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon } from "lucide-react";
import { mutateRequest, postFetcher } from "@/lib/swr";
import { useApps } from "@/hooks/apps";
import { useState } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { z } from "zod";
import { ListLoading } from "@/components/list/status-text";

const validationSchema = z.object({
  pid: z.string().min(1, "app is required"),
  versionName: z.string().min(1, "major is required"),
  versionCode: z.string().min(1, "version is required"),
});

// `pid`: when rendered inside an app's page the app is fixed — preselect it
// and hide the app selector.
const CreateVersion = ({ pid }: { pid?: string }) => {
  const Apps = useApps();
  const [open, setOpen] = useState(false);
  const { trigger, isMutating, error } = useSWRMutation(
    "/versions",
    postFetcher,
    {
      onSuccess: () => {
        form.reset();
        setOpen(false);
        mutateRequest("/versions");
      },
    }
  );

  const defaultValues = {
    pid: pid ?? "",
    versionName: "",
    versionCode: "",
  };

  const form = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  // useForm only reads defaultValues on first mount; the component instance
  // survives app-to-app navigation (/apps/1 → /apps/3), which left a stale
  // pid in the form. Re-seed on every open.
  const onOpenChange = (o: boolean) => {
    if (o) form.reset(defaultValues);
    setOpen(o);
  };

  const handleSubmit = (data: z.infer<typeof validationSchema>) => {
    if (isMutating) return;
    trigger(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">
          <PlusIcon className="mr-2 h-4 w-4" /> New Version
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new version</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {!pid && (
              <div className="space-y-2">
                <MyFormSelect
                  name="pid"
                  form={form}
                  label="App"
                  items={Apps.map((a) => ({ ...a, code: a.code ?? String(a.id) }))}
                  showCode
                  placeholder="Select an app"
                />
              </div>
            )}
            <div className="space-y-2">
              <MyFormInput
                name="versionName"
                form={form}
                label="Version name"
                placeholder="major.version.code8.app (eg: 3.5.38.1-fix)"
              />
            </div>
            <div className="space-y-2">
              <MyFormInput
                name="versionCode"
                form={form}
                label="Version code"
                placeholder="eg:1300530"
              />
            </div>
            {isMutating ? (
              <ListLoading>Saving…</ListLoading>
            ) : (
              <Button
                type="submit"
                disabled={
                  !form.formState.isValid || form.formState.isSubmitting
                }
              >
                Create
              </Button>
            )}
            {error && <div className="text-destructive">{error.message}</div>}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateVersion;
